import * as React from 'react';

import {OnboardingModal as OnboardingModalShell} from 'shells/onboarding-modal/onboarding-modal';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {GetSessionUser_loggedInUser as User} from 'nutshell-graphql-types';

import {useGetCalendarSync} from '../../setup/calendar/graphql/hooks/use-get-calendar-sync';

import {getArt} from './consts';
import {
    Introduction,
    Connect,
    Connected,
    TeamsAdminApproval,
    CalendarSyncCta,
    ConnectionNextStepsCta,
} from './pages';

type Props = {
    isConnected: boolean;
    isEnabled: boolean;
    userEmail: string;
    isLoading: boolean;
    refetchIntegration: () => Promise<any>;
    onClose: () => void;
    onSuccess?: () => void;
    connectIntegration: (onStart?: () => void, onSuccess?: () => void) => void;
    errorMessage: string | null | undefined;
    setError: (error?: string | null | undefined) => void;
    serviceType: ExternalServiceType;
    user: User;
    enableIntegration: () => void;
    disableIntegration: () => void;
    // If true, show the modal immediately so the user can connect their meetings quicker
    bypassLoadingState: boolean;
};

export function OnboardingModal(props: Props) {
    const [pageAutoAdvance, setPageAutoAdvance] = React.useState(false);
    const [pageAutoPrevious, setPageAutoPrevious] = React.useState(false);
    const {calendarSync, isLoading: isLoadingCalendarSync} = useGetCalendarSync();

    const hideErrorMessage = () => {
        props.setError(null);
    };

    const goToCalendarSetup = () => {
        window.location.href = '/my-account/calendar';
    };

    const art = getArt(props.serviceType);

    const pages = [
        {
            id: 'Introduction',
            body: <Introduction serviceType={props.serviceType} />,
            art,
        },
    ];

    if (props.serviceType === 'MICROSOFT_TEAMS') {
        pages.push({
            id: 'TeamsAdminApproval',
            body: <TeamsAdminApproval />,
            art,
        });
    }

    pages.push(
        ...[
            {
                id: 'Connect',
                body: (
                    <Connect
                        setPageAutoAdvance={setPageAutoAdvance}
                        setPageAutoPrevious={setPageAutoPrevious}
                        onSuccess={props.onSuccess}
                        errorMessage={props.errorMessage}
                        hideErrorMessage={hideErrorMessage}
                        isConnected={props.isConnected}
                        connectIntegration={props.connectIntegration}
                        serviceType={props.serviceType}
                    />
                ),
                art,
                // @ts-expect-error
                nextDisabled: !props.isConnected,
            },
            {
                id: 'Connected',
                body: (
                    <Connected
                        isConnected={props.isConnected}
                        isEnabled={props.isEnabled}
                        userEmail={props.userEmail}
                        setPageAutoPrevious={setPageAutoPrevious}
                        isLoading={props.isLoading}
                        refetchIntegration={props.refetchIntegration}
                        serviceType={props.serviceType}
                        user={props.user}
                        onEnableClick={props.enableIntegration}
                        onDisableClick={props.disableIntegration}
                    />
                ),
                art,
                nextDisabled: !props.isConnected,
                secondaryButtonDisabled: props.isConnected,
            },
        ]
    );

    if (
        (!calendarSync || !calendarSync.isEnabled) &&
        !NutClientConfig.hasImprovedEmailCalendarSyncFF
    ) {
        pages.push({
            id: 'CalendarSyncCta',
            body: <CalendarSyncCta serviceType={props.serviceType} />,
            art,
            // @ts-expect-error
            nextButtonText: 'Go to calendar setup',
            secondaryButtonText: 'Not now',
            onClickNextButton: goToCalendarSetup,
            onClickSecondaryButton: props.onClose,
        });
    }

    if (NutClientConfig.hasImprovedEmailCalendarSyncFF) {
        pages.push({
            id: 'ConnectNextSteps',
            body: <ConnectionNextStepsCta onClose={props.onClose} />,
            art,
            // @ts-expect-error
            nextButtonText: 'Done',
            onClickNextButton: props.onClose,
        });
    }

    if (isLoadingCalendarSync && !props.bypassLoadingState) {
        return null;
    }

    return (
        <OnboardingModalShell
            trapFocus={false}
            lastButtonText='Done'
            pages={pages}
            onClose={props.onClose}
            onFinish={props.onClose}
            pageAutoAdvance={pageAutoAdvance}
            pageAutoPrevious={pageAutoPrevious}
            maxHeight={600}
            closeButton={false}
        />
    );
}
